import React, { useState } from 'react';
import { FileText, Shield, Users, AlertTriangle } from 'lucide-react';

export function TermsPage() {
  const [activeTab, setActiveTab] = useState('user-terms');

  const tabs = [
    { id: 'user-terms', title: 'User Terms', icon: FileText },
    { id: 'vendor-terms', title: 'Vendor Terms', icon: AlertTriangle },
    { id: 'privacy', title: 'Privacy Policy', icon: Shield },
    { id: 'aml-kyc', title: 'AML/KYC Policy', icon: Users },
  ];

  return (
    <div className="max-w-6xl mx-auto py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Legal & Compliance</h1>
        <p className="text-gray-400">Terms of service, policies, and compliance information for TapTapUp</p>
      </div>

      {/* Tabs Navigation */}
      <div className="mb-6">
        <nav className="flex flex-wrap gap-2 border-b border-gray-700">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-3 rounded-t-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#61CE70] text-white'
                  : 'text-gray-400 hover:text-white hover:bg-[#1A1F2B]'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span className="text-sm font-medium">{tab.title}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-[#0F1419] border border-gray-700 rounded-lg p-8">
        {activeTab === 'user-terms' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">User Terms of Service</h2>
              <p className="text-gray-400 mb-6">Effective Date: August 1, 2025</p>
            </div>

            <div className="space-y-6 text-gray-300">
              <section>
                <h3 className="text-xl font-semibold text-white mb-3">1. Platform Role</h3>
                <p>
                  TapTapUp is a neutral platform connecting users with third-party vendors. We do not manage, control, or endorse vendors, nor provide financial advice or custodial services.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-3">2. User Responsibilities</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Verify the legitimacy of chosen vendors.</li>
                  <li>Accept risks of financial transactions.</li>
                  <li>Conduct due diligence before transactions.</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-3">3. Payments & Fund Transfers</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Payments go directly to vendors; TapTapUp does not hold funds unless required for compliance.</li>
                  <li>No escrow or custodial services provided.</li>
                  <li>Users confirm payments are lawful and not tied to illegal activities.</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-3">4. Stablecoin Purchases</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Purchase approved stablecoins (e.g., USDT, USDC) from TapTapUp reserves.</li>
                  <li>Requires KYC/AML verification.</li>
                  <li>Users must provide accurate wallet addresses; purchases are non-refundable once delivered.</li>
                  <li>Transactions may be delayed or rejected for fraud or regulatory issues.</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-3">5. Limitation of Liability</h3>
                <p>
                  TapTapUp is not liable for vendor-related disputes or losses. We may hold or reverse suspicious transactions. Liability is limited to direct damages, excluding indirect losses.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-3">6. Governing Law</h3>
                <p>
                  Governed by Wyoming law, with disputes handled in Wyoming courts.
                </p>
              </section>
            </div>
          </div>
        )}

        {activeTab === 'vendor-terms' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">Vendor Terms of Service</h2>
              <p className="text-gray-400 mb-6">Effective Date: August 1, 2025</p>
            </div>

            <div className="space-y-6 text-gray-300">
              <section>
                <h3 className="text-xl font-semibold text-white mb-3">1. Independent Entity</h3>
                <p>
                  TapTapUp is a neutral facilitator, not a partner or agent of any vendor.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-3">2. vendor Responsibilities</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Manage customer disputes and refunds independently.</li>
                  <li>Cooperate with investigations into fraud or disputes.</li>
                  <li>Ensure services comply with applicable laws.</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-3">3. Prohibited Services</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Unlicensed financial advising.</li>
                  <li>High-risk gambling or betting schemes.</li>
                  <li>Fraudulent investment programs (e.g., Ponzi schemes).</li>
                  <li>Services in sanctioned jurisdictions.</li>
                  <li>Illegal content or activities.</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-3">4. Fund Holds & Termination</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>TapTapUp may freeze or reverse transactions for suspected fraud.</li>
                  <li>Accounts may be suspended for violations or non-cooperation.</li>
                  <li>Funds may be returned to customers in cases of misconduct.</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-3">5. Indemnification</h3>
                <p>
                  vendors indemnify TapTapUp against claims from their actions. Disputes are resolved via arbitration in Wyoming, USA.
                </p>
              </section>
            </div>
          </div>
        )}

        {activeTab === 'privacy' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">Privacy Policy</h2>
              <p className="text-gray-400 mb-6">Effective Date: August 1, 2025</p>
            </div>

            <div className="space-y-6 text-gray-300">
              <section>
                <h3 className="text-xl font-semibold text-white mb-3">1. Information We Collect</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Name, email, phone number.</li>
                  <li>Government-issued ID and selfie (via third-party KYC provider).</li>
                  <li>Transaction details (amount, time, wallet address).</li>
                  <li>IP address and device metadata for analytics and fraud prevention.</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-3">2. Third-Party Providers</h3>
                <p>
                  Certified third-party KYC/AML providers handle sensitive identity data, sharing only verification outcomes with TapTapUp.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-3">3. Data Usage</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Verify identity for compliance.</li>
                  <li>Prevent fraud and secure transactions.</li>
                  <li>Facilitate stablecoin purchases.</li>
                  <li>Comply with legal obligations.</li>
                </ul>
                <p className="mt-4">We do not sell or trade your personal data.</p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-3">4. Data Security & Retention</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Encryption and access controls for data security.</li>
                  <li>Transaction records retained for 5 years.</li>
                  <li>KYC results kept only as required for compliance.</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-3">5. Your Rights</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access or correct personal data.</li>
                  <li>Request data deletion (subject to legal requirements).</li>
                  <li>Contact: privacy@taptapup.com.</li>
                </ul>
              </section>
            </div>
          </div>
        )}

        {activeTab === 'aml-kyc' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">AML/KYC Policy</h2>
              <p className="text-gray-400 mb-6">Effective Date: August 1, 2025</p>
            </div>

            <div className="space-y-6 text-gray-300">
              <section>
                <h3 className="text-xl font-semibold text-white mb-3">1. Purpose & Scope</h3>
                <p>
                  TapTapUp prevents money laundering and illicit activities through KYC, transaction monitoring, and sanctions screening.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-3">2. Customer Due Diligence</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Basic KYC: Name, email, phone, ID, selfie.</li>
                  <li>Enhanced Due Diligence: Proof of address, source of funds for high-risk users.</li>
                  <li>Ongoing sanctions screening (e.g., OFAC).</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-3">3. Transaction Monitoring</h3>
                <p>
                  Third-party providers screen transactions for suspicious activity, reported to authorities as needed. Contact: compliance@taptapup.com.
                </p>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-3">4. Prohibited Activities</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Sanctioned jurisdiction transactions.</li>
                  <li>Unlicensed securities or investments.</li>
                  <li>Fraud, scams, or pyramid schemes.</li>
                  <li>Illegal goods or services.</li>
                </ul>
              </section>

              <section>
                <h3 className="text-xl font-semibold text-white mb-3">5. Recordkeeping</h3>
                <p>
                  KYC and transaction records are stored securely for at least 5 years, accessible to regulators.
                </p>
              </section>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}